/**
 * The one place that talks to the backend.
 *
 * Auth is a DOUBLE RAIL, and both halves matter (see the plan, §3):
 *
 *  - On the web and in the installed PWA, the browser holds an HttpOnly
 *    `session_token` cookie. JavaScript cannot read it, which is the point:
 *    a token this layer cannot see is a token XSS cannot steal. Every request
 *    therefore sends `credentials: "include"`.
 *  - Inside the Capacitor native shell the origin is `https://localhost`, so
 *    that cookie is a third-party cookie and gets dropped. There, and only
 *    there, we fall back to `Authorization: Bearer`.
 *
 * The backend prefers Bearer over cookie, so sending both is safe and the same
 * build works in both places. The token, when we have one, is kept in memory
 * plus the platform's secure storage — never localStorage.
 *
 * The cookie rail carries a third obligation: a CSRF double-submit header on
 * every state-changing request (see ./csrf.ts). Without it, production —
 * which the backend refuses to start without CSRF_PROTECTION_ENFORCED=1 —
 * answers 403 to every write.
 */
import {
  ensureCsrfToken, isCsrfRejection, isGuardedMethod, readCsrfCookie,
} from "./csrf";
import { attemptRefresh, REFRESH_PATH } from "./refresh";
import type { paths } from "./schema";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly payload: unknown = null,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** 401: not signed in, or the session expired. */
  get isUnauthenticated() {
    return this.status === 401;
  }

  /** 403: signed in, but this role/block may not do it. */
  get isForbidden() {
    return this.status === 403;
  }

  /** Nothing reached the server — offline, DNS, TLS. */
  get isNetwork() {
    return this.status === 0;
  }
}

let bearerToken: string | null = null;

export function setBearerToken(token: string | null) {
  bearerToken = token;
}

/** French copy for the error banners. The backend already speaks French for
 *  domain errors; these cover the transport-level cases it cannot. */
export function messageFor(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.isNetwork) return "Connexion impossible. Vérifiez votre réseau.";
    if (error.isUnauthenticated) return "Session expirée. Reconnectez-vous.";
    if (error.isForbidden) return "Vous n'avez pas l'autorisation pour cette action.";
    return error.message;
  }
  return "Une erreur inattendue est survenue.";
}

function extractDetail(payload: unknown, status: number): string {
  if (payload && typeof payload === "object" && "detail" in payload) {
    const detail = (payload as { detail: unknown }).detail;
    if (typeof detail === "string") return detail;
    if (detail) return JSON.stringify(detail);
  }
  return `http_${status}`;
}

type Options = {
  method?: string;
  body?: unknown;
  /** Send as multipart/form-data — the login endpoint is OAuth2-style. */
  form?: FormData;
  signal?: AbortSignal;
  /**
   * Replay protection for money writes (FR-082). The SAME key must be reused
   * across retries of one user action: a cashier who taps "Enregistrer" twice,
   * or a request retried after a timeout on a 3G connection, must produce ONE
   * payment. Generate it when the form opens, not when it submits.
   */
  idempotencyKey?: string;
  /** Internal: set on the single replay after a successful silent refresh, so
   *  a session that is genuinely dead cannot loop. */
  _refreshed?: boolean;
};

export async function request<T>(path: string, opts: Options = {}): Promise<T> {
  const method = (opts.method ?? (opts.body || opts.form ? "POST" : "GET")).toUpperCase();
  // The CSRF guard only applies to the COOKIE rail. A bearer-authenticated
  // request used no cookie, so the server exempts it — seeding a token there
  // would be a pointless round-trip on a phone.
  const needsCsrf = isGuardedMethod(method) && !bearerToken;

  if (needsCsrf) await ensureCsrfToken();

  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = {};
    if (bearerToken) headers.Authorization = `Bearer ${bearerToken}`;
    if (opts.body !== undefined && !opts.form) headers["Content-Type"] = "application/json";
    if (opts.idempotencyKey) headers["Idempotency-Key"] = opts.idempotencyKey;
    if (needsCsrf) {
      // Read at send time, never cached: the cookie outlives the session by
      // design (24h) and a retry must pick up a freshly seeded value.
      const token = readCsrfCookie();
      if (token) headers["X-CSRF-Token"] = token;
    }
    try {
      return await fetch(path, {
        method,
        headers,
        body: opts.form ?? (opts.body !== undefined ? JSON.stringify(opts.body) : undefined),
        // Required for the HttpOnly session cookie, in the browser AND in a
        // WebView where the default "same-origin" would silently drop it.
        credentials: "include",
        signal: opts.signal,
      });
    } catch (cause) {
      throw new ApiError(`network: ${(cause as Error)?.message ?? cause}`, 0);
    }
  };

  let response = await send();
  let payload = await readBody(response);

  // Backstop for the narrow race where the cookie vanishes between the
  // pre-flight seed and this send (see ./csrf.ts — the seed handles the
  // ordinary missing-cookie case). Replaying is safe: the retry carries the
  // SAME Idempotency-Key, so a write the server actually accepted cannot be
  // duplicated by it.
  if (needsCsrf && isCsrfRejection(response.status, payload)) {
    await ensureCsrfToken(true);
    response = await send();
    payload = await readBody(response);
  }

  // A 401 usually means the short-lived access token aged out, not that the
  // user is gone. Refresh once, silently, and replay — otherwise a cashier
  // loses a half-typed payment to a token that expired between two keystrokes.
  //
  // Never on the refresh route itself (that would loop), and never twice
  // (`_refreshed`): after a refresh that still 401s, the session really is
  // over and the caller should be told.
  if (
    response.status === 401
    && !opts._refreshed
    && !path.includes(REFRESH_PATH)
  ) {
    if (await attemptRefresh(setBearerToken)) {
      return request<T>(path, { ...opts, _refreshed: true });
    }
  }

  if (!response.ok) {
    throw new ApiError(extractDetail(payload, response.status), response.status, payload);
  }
  return (payload ?? {}) as T;
}

async function readBody(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

/** Typed helper for the paths openapi-typescript knows about. */
export function get<P extends keyof paths>(path: P, signal?: AbortSignal) {
  return request<unknown>(path as string, { signal });
}
